// Tenantra Inc. - Priority Support Page (Microsoft 365 Admin Style)
// Ticket tracking with clean list view - matches Properties/Tenants tabs
import { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw, ChevronRight, Send, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCurrentUser } from '@/services/auth';
import { formatDate } from '@/utils/date';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api from '@/services/api';
import { toast } from 'sonner';

// Status config - simple dot colors like Properties page
const STATUS_CONFIG = {
  open: { label: 'Open', dotColor: 'bg-blue-500', textColor: 'text-blue-600' },
  in_progress: { label: 'In Progress', dotColor: 'bg-purple-500', textColor: 'text-purple-600' },
  waiting_on_customer: { label: 'Awaiting Reply', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  resolved: { label: 'Resolved', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  closed: { label: 'Closed', dotColor: 'bg-slate-400', textColor: 'text-slate-500' }
};

// Priority config
const PRIORITY_CONFIG = {
  low: { label: 'Low', textColor: 'text-slate-500' },
  medium: { label: 'Medium', textColor: 'text-blue-600' },
  high: { label: 'High', textColor: 'text-amber-600' },
  urgent: { label: 'Urgent', textColor: 'text-red-600' }
};

// Empty State - minimal like Properties
function EmptyState({ onCreateClick }) {
  return (
    <div className="text-center py-12">
      <h3 className="text-sm font-medium text-slate-900 mb-1">No support tickets</h3>
      <p className="text-xs text-slate-400 max-w-sm mx-auto mb-4">
        Create a ticket to get priority support from our team.
      </p>
      <button onClick={onCreateClick} className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" />
        Create ticket
      </button>
    </div>
  );
}

// Ticket Detail View
function TicketDetail({ ticketId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/support/${ticketId}`);
      setTicket(response.data.ticket);
      setReplies(response.data.replies || []);
    } catch (err) {
      toast.error('Failed to load ticket');
      onBack();
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/${ticketId}/reply`, { message: newReply });
      setNewReply('');
      fetchTicket();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!confirm('Close this ticket?')) return;
    try {
      await api.put(`/support/${ticketId}/close`);
      toast.success('Ticket closed');
      fetchTicket();
    } catch (err) {
      toast.error('Failed to close ticket');
    }
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  if (!ticket) return null;

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;

  return (
    <div className="space-y-5">
      {/* Back link */}
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" />
        Back to tickets
      </button>

      {/* Ticket header */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-slate-400">{ticket.ticket_number}</p>
              <h2 className="text-base font-semibold text-slate-800 mt-0.5">{ticket.subject}</h2>
              <div className="flex items-center gap-3 mt-2 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotColor}`}></span>
                  <span className={statusConfig.textColor}>{statusConfig.label}</span>
                </span>
                <span className="text-slate-300">·</span>
                <span className={priorityConfig.textColor}>{priorityConfig.label}</span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">{formatDate(ticket.created_at)}</span>
              </div>
            </div>
            {ticket.status !== 'closed' && (
              <button onClick={handleClose} className="text-xs text-slate-400 hover:text-red-600">
                Close
              </button>
            )}
          </div>
        </div>

        {/* Original message */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
        </div>

        {/* Replies */}
        {replies.length > 0 && (
          <div className="divide-y divide-slate-100">
            {replies.map((reply) => (
              <div key={reply.id} className={`px-4 py-3 ${reply.is_staff ? 'bg-teal-50/30' : ''}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-medium ${reply.is_staff ? 'text-teal-700' : 'text-slate-700'}`}>
                    {reply.is_staff ? 'Support Team' : 'You'}
                  </span>
                  <span className="text-xs text-slate-400">{new Date(reply.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{reply.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reply form */}
        {ticket.status !== 'closed' && (
          <form onSubmit={handleReply} className="px-4 py-3 border-t border-slate-200">
            <Textarea
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              placeholder="Reply"
              className="min-h-[60px] resize-none text-sm mb-2 bg-slate-50 border-slate-200"
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={sending || !newReply.trim()} size="sm" className="h-8 bg-teal-600 hover:bg-teal-700 text-white">
                {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Send className="w-3 h-3 mr-1.5" />}
                Send
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Create Ticket Form
function CreateTicketForm({ onBack, onSuccess }) {
  const user = getCurrentUser();
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/support', formData);
      toast.success('Ticket created');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" />
        Back to tickets
      </button>

      <div>
        <h1 className="text-sm font-semibold text-slate-800">New Support Ticket</h1>
        <p className="text-xs text-slate-400">Priority response within 4 hours (business hours)</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger className="h-9 text-sm bg-slate-50 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="verification">Verification</SelectItem>
                  <SelectItem value="dispute">Dispute</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                <SelectTrigger className="h-9 text-sm bg-slate-50 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Subject</Label>
            <Input
              value={formData.subject}
              onChange={(e) => setFormData({...formData, subject: e.target.value})}
              required
              minLength={5}
              className="h-9 text-sm bg-slate-50 border-slate-200"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              minLength={20}
              className="min-h-[100px] resize-none text-sm bg-slate-50 border-slate-200"
            />
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Ticket
          </Button>
        </form>
      </div>
    </div>
  );
}

// Main Page
export default function SupportPage() {
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/support');
      setTickets(response.data);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show detail view
  if (view === 'detail' && selectedTicketId) {
    return (
      <TicketDetail
        ticketId={selectedTicketId}
        onBack={() => { setSelectedTicketId(null); setView('list'); fetchTickets(); }}
      />
    );
  }

  // Show create form
  if (view === 'create') {
    return (
      <CreateTicketForm
        onBack={() => setView('list')}
        onSuccess={() => { setView('list'); fetchTickets(); }}
      />
    );
  }

  // Filter tickets
  const filteredTickets = tickets.filter(t => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'active' && ['closed', 'resolved'].includes(t.status)) return false;
      if (statusFilter !== 'active' && t.status !== statusFilter) return false;
    }
    if (searchTerm) {
      return t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
             t.ticket_number.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  // Stats
  const stats = {
    total: tickets.length,
    open: tickets.filter(t => ['open', 'in_progress', 'waiting_on_customer'].includes(t.status)).length,
    resolved: tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-sm font-semibold text-slate-800">Priority Support</h1>
        <p className="text-xs text-slate-400">Response within 4 hours during business hours</p>
      </div>

      {/* Stats Row - like Properties */}
      {tickets.length > 0 && (
        <div className="flex items-center gap-5 py-2 border-b border-slate-200 flex-wrap text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-700">{stats.total}</span>
            <span className="text-xs text-slate-400">Total</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-blue-600">{stats.open}</span>
            <span className="text-xs text-slate-400">Open</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-emerald-600">{stats.resolved}</span>
            <span className="text-xs text-slate-400">Resolved</span>
          </div>
        </div>
      )}

      {/* Actions Row */}
      <div className="flex items-center gap-4">
        <button onClick={() => setView('create')} className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1.5 hover:underline">
          <Plus className="w-3.5 h-3.5" />
          Create ticket
        </button>
        <button onClick={fetchTickets} className="text-xs text-slate-400 hover:text-slate-600 font-medium inline-flex items-center gap-1.5 hover:underline">
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tickets"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 pl-9 text-sm bg-white border-slate-200"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 h-9 text-sm bg-white border-slate-200">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tickets Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-slate-200 border-t-teal-600 rounded-full animate-spin" />
        </div>
      ) : filteredTickets.length === 0 ? (
        searchTerm || statusFilter !== 'all' ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <h3 className="text-base font-medium text-slate-800 mb-1">No results</h3>
                <button onClick={() => { setSearchTerm(''); setStatusFilter('all'); }} className="text-sm text-teal-600 hover:text-teal-700 font-medium">
              Clear filters
            </button>
          </div>
        ) : (
          <EmptyState onCreateClick={() => setView('create')} />
        )
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wide">
            <div className="col-span-1">ID</div>
            <div className="col-span-5">Subject</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Priority</div>
            <div className="col-span-2">Status</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-slate-100">
            {filteredTickets.map((ticket) => {
              const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
              const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;

              return (
                <div
                  key={ticket.id}
                  onClick={() => { setSelectedTicketId(ticket.id); setView('detail'); }}
                  className="lens lens-row grid grid-cols-12 gap-4 px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors items-center"
                >
                  <div className="col-span-12 sm:col-span-1">
                    <span className="text-xs text-slate-400">{ticket.ticket_number.split('-').pop()}</span>
                  </div>
                  <div className="col-span-12 sm:col-span-5">
                    <div className="text-xs font-medium text-slate-800 truncate">{ticket.subject}</div>
                    <div className="sm:hidden text-[11px] text-slate-400 mt-0.5">{formatDate(ticket.created_at)}</div>
                  </div>
                  <div className="hidden sm:block col-span-2">
                    <span className="text-xs text-slate-500 capitalize">{ticket.category}</span>
                  </div>
                  <div className="hidden sm:block col-span-2">
                    <span className={`text-xs ${priorityConfig.textColor} capitalize`}>{ticket.priority}</span>
                  </div>
                  <div className="hidden sm:flex col-span-2 items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotColor}`}></span>
                    <span className={`text-xs ${statusConfig.textColor}`}>{statusConfig.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
